<template>
  <div 
    class="kpi-card" 
    :class="[cardClass, { 'kpi-card-gradient': useGradient }]" 
    :style="cardStyle"
    @click="handleClick"
  >
    <div class="icon" :class="iconClass">{{ icon }}</div>
    <div class="label">{{ title }}</div>
    <div :class="['value', valueClass, dynamicValueClass]">
      {{ displayValue }}
      <span v-if="trend !== null" class="trend-indicator" :class="trendClass">
        {{ trend > 0 ? '↑' : trend < 0 ? '↓' : '→' }} {{ Math.abs(trend) }}%
      </span>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    icon: { type: String, default: '📊' },
    title: { type: String, required: true },
    value: { type: [String, Number], required: true },
    valueClass: { type: String, default: '' },
    trend: { type: Number, default: null },
    thresholds: {
      type: Object,
      default: () => ({ low: 30, medium: 600, high: 1800 })
    },
    inverse: { type: Boolean, default: false },
    useGradient: { type: Boolean, default: false },
    cardClass: { type: String, default: '' },
    cardStyle: { type: Object, default: () => ({}) },
    numericValue: { type: Number, default: null },
    iconClass: { type: String, default: '' },
    /** When true, forces the value color to the neutral info color class regardless of thresholds. */
    forceInfo: { type: Boolean, default: false }
  },
  data() {
    return {
      isMobile: false
    };
  },
  mounted() {
    this.checkIfMobile();
    window.addEventListener('resize', this.checkIfMobile);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkIfMobile);
  },
  computed: {
    displayValue() {
      // Priority: if a non-empty string value is provided, always show it (text KPIs)
      const isString = typeof this.value === 'string';
      if (isString) {
        const trimmed = this.value.trim();
        if (trimmed) return trimmed;
      }
      // If numeric context only, abbreviate on mobile when numericValue is provided
      if (this.isMobile && this.numericValue !== null) {
        return this.$utils.formatAbbreviatedNumber(this.numericValue);
      }
      // Fallback to raw value (could be number or empty string)
      return this.value;
    },
    dynamicValueClass() {
      // Optional override: force neutral/info color regardless of thresholds
      if (this.forceInfo) return 'text-info';
      // If numericValue is provided, use it to determine color class
      if (this.numericValue !== null) {
        return this.$colorIndicator.getValueColorClass(
          this.numericValue,
          this.thresholds.low,
          this.thresholds.medium,
          this.thresholds.high,
          this.inverse
        );
      }
      return '';
    },
    trendClass() {
      if (this.trend === null) return '';
      return this.$colorIndicator.getTrendColorClass(this.trend, this.inverse);
    }
  },
  methods: {
    checkIfMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
    handleClick(e) {
      // Provide haptic feedback when card is clicked
      this.$hapticFeedback.medium();
      // Forward the native MouseEvent so parent modifiers like .stop/.prevent work correctly
      this.$emit('click', e);
    }
  }
};
</script>

<style scoped>
.kpi-card {
  background: var(--card-bg);
  padding: 1.25rem;
  border-radius: var(--radius-card);
  text-align: center;
  flex: 1;
  min-width: 140px; /* Ensure minimum width on mobile */
  box-shadow: var(--shadow, 0 3px 10px rgba(0,0,0,0.08));
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

.kpi-card-gradient {
  color: white;
}

.icon { 
  font-size: 2rem; 
  margin-bottom: 0.5rem;
}

.label { 
  margin-top: 0.5rem; 
  font-weight: bold;
  font-size: 0.9rem;
  color: inherit;
}

.value { 
  font-size: 1.8rem; 
  margin-top: 0.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}

.trend-indicator {
  font-size: 0.9rem;
  margin-left: 0.5rem;
  font-weight: normal;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .kpi-card {
    padding: 1rem;
    min-width: 110px;
    margin-bottom: 0.5rem;
  }

  .icon {
    font-size: 1.5rem;
    margin-bottom: 0.3rem;
  }

  .label {
    font-size: 0.8rem;
    margin-top: 0.3rem;
  }

  .value {
    font-size: 1.4rem;
    margin-top: 0.3rem;
  }

  .trend-indicator {
    font-size: 0.7rem;
    margin-left: 0.3rem;
  }
}

@media (max-width: 480px) {
  .kpi-card {
    padding: 0.75rem;
    min-width: 90px;
  }

  .icon {
    font-size: 1.2rem;
    margin-bottom: 0.2rem;
  }

  .label {
    font-size: 0.7rem;
    margin-top: 0.2rem;
  }

  .value {
    font-size: 1.2rem;
    margin-top: 0.2rem;
  }
}

/* Color classes */
.text-success { color: #28a745; }
.text-warning { color: #ffc107; }
.text-danger { color: #dc3545; }
.text-info { color: #17a2b8; }
.text-primary { color: #007bff; }
.text-secondary { color: #6c757d; }

/* Background color classes */
.bg-success { background-color: #28a745; color: white; }
.bg-warning { background-color: #ffc107; color: #212529; }
.bg-danger { background-color: #dc3545; color: white; }
.bg-info { background-color: #17a2b8; color: white; }
.bg-primary { background-color: #007bff; color: white; }
.bg-secondary { background-color: #6c757d; color: white; }

/* Gradient backgrounds */
.bg-gradient-success {
  background: linear-gradient(45deg, #28a745, #20c997);
  color: white;
}
.bg-gradient-danger {
  background: linear-gradient(45deg, #dc3545, #e83e8c);
  color: white;
}
.bg-gradient-warning {
  background: linear-gradient(45deg, #ffc107, #fd7e14);
  color: #212529;
}
.bg-gradient-info {
  background: linear-gradient(45deg, #17a2b8, #007bff);
  color: white;
}

/* Legacy class for backward compatibility */
.danger { color: #dc3545; }
</style>
